<template>
  <div class="online-test">
    <el-card class="panel">
      <template #header>
        <div class="card-header">
          <el-icon><FolderOpened /></el-icon>
          <span>在线验证码识别</span>
        </div>
      </template>

      <!-- 使用栅格布局：左侧表单，右侧上传+按钮 -->
      <el-row :gutter="20">
        <!-- 左侧：任务名称和任务描述 -->
        <el-col :xs="24" :sm="12" :md="12">
          <div class="left-form">
            <div class="form-row">
              <label>任务名称：</label>
              <el-input v-model="taskName" placeholder="【选填】例如：2025-01 登录验证码批量识别" />
            </div>
            <div class="form-row">
              <label>任务描述：</label>
              <el-input v-model="taskDesc" type="textarea" rows="3" placeholder="【选填】简要说明验证码来源等" />
            </div>
          </div>
        </el-col>

        <!-- 右侧：图片上传 + 操作按钮 -->
        <el-col :xs="24" :sm="12" :md="12">
          <div class="right-content">
            <!-- 识别模式 -->
            <div class="form-row mode-row">
              <label>识别模式：</label>
              <el-radio-group v-model="mode">
                <el-radio value="filename-compare">文件名比对模式</el-radio>
                <el-radio value="normal">普通模式</el-radio>
              </el-radio-group>
            </div>

            <!-- 图片上传区域 -->
            <div class="upload-area">
              <label>选择图片（可多选）：</label>
              <div class="upload-hint" v-if="fileList.length > 6">
                <el-text type="info">图片较多，可滑动查看</el-text>
              </div>
              <el-upload
                  ref="uploadRef"
                  v-model:file-list="fileList"
                  action="#"
                  :auto-upload="false"
                  :multiple="true"
                  accept="image/*"
                  list-type="picture-card"
                  :on-preview="handlePreview"
                  :on-remove="handleRemove"
                  :before-upload="handleBeforeUpload"
              >
                <el-icon><Plus /></el-icon>
                <template #file="{ file }">
                  <div>
                    <img class="el-upload-list__item-thumbnail" :src="file.url" alt="" />
                    <span class="el-upload-list__item-actions">
                      <span class="el-upload-list__item-preview" @click="handlePreview(file)">
                        <el-icon><ZoomIn /></el-icon>
                      </span>
                      <span class="el-upload-list__item-delete" @click="handleRemove(file)">
                        <el-icon><Delete /></el-icon>
                      </span>
                    </span>
                  </div>
                </template>
              </el-upload>
            </div>

            <!-- 操作按钮 -->
            <div class="actions">
              <el-button type="primary" @click="handleCreateTask" :loading="creating" :disabled="fileList.length === 0">
                开始批量识别
              </el-button>
              <el-button @click="reset" :disabled="creating">重置</el-button>
            </div>
          </div>
        </el-col>
      </el-row>

      <!-- 任务进度展示 -->
      <div v-if="createdTask" class="task-progress">
        <el-card shadow="never">
          <div class="progress-header">
            <span class="task-name">{{ createdTask.taskName }}</span>
            <el-tag :type="taskStatusTag(createdTask.status)">{{ taskStatusText(createdTask.status) }}</el-tag>
          </div>
          <div class="progress-stats">
            <div>已处理：{{ taskProgress.processed }} / {{ taskProgress.total }}</div>
            <el-progress :percentage="taskProgress.percentage" :status="taskProgress.status" />
          </div>
          <div class="progress-detail">
            <span>成功：{{ taskProgress.success }}</span>
            <span>失败：{{ taskProgress.fail }}</span>
          </div>
          <div v-if="createdTask.status === 2" class="progress-footer">
            <el-button type="primary" @click="viewBatchLogs(createdTask.batchId)">查看该批次日志</el-button>
          </div>
        </el-card>
      </div>

      <!-- 错误提示 -->
      <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" style="margin-top: 16px" />
    </el-card>
  </div>
</template>

<script setup>
import {ref, onUnmounted} from 'vue'
import {useRouter} from 'vue-router'
import {uploadBatchTask, getBatchDetail} from '@/api/captcha'
import {ElMessage} from 'element-plus'
import {FolderOpened, Plus, Delete, ZoomIn} from '@element-plus/icons-vue'

const router = useRouter()

// 表单数据
const taskName = ref('')
const taskDesc = ref('')
// 修改这里，默认模式改为文件名比对模式
const mode = ref('filename-compare')
const fileList = ref([])
const creating = ref(false)
const errorMsg = ref('')
const uploadRef = ref(null)

// 任务相关
const createdTask = ref(null)
const taskProgress = ref({
  processed: 0,
  total: 0,
  percentage: 0,
  success: 0,
  fail: 0,
  status: 'primary'
})
const pollingTimer = ref(null)

// 文件大小限制（与后端一致：200MB）
const MAX_FILE_SIZE = 200 * 1024 * 1024

// 上传前检查单个文件大小
const handleBeforeUpload = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    ElMessage.error(`文件 ${file.name} 大小超过 200MB 限制，已自动跳过`)
    return false
  }
  return true
}

// 移除文件
const handleRemove = (file) => {
  const index = fileList.value.findIndex(f => f.uid === file.uid)
  if (index !== -1) fileList.value.splice(index, 1)
}

// 预览图片
const handlePreview = (file) => {
  window.open(file.url, '_blank')
}

// 开始轮询任务进度
const startPolling = (batchId) => {
  if (pollingTimer.value) clearInterval(pollingTimer.value)
  pollingTimer.value = setInterval(async () => {
    try {
      const resp = await getBatchDetail(batchId)
      if (resp.data.code === 200) {
        const task = resp.data.data
        createdTask.value = task
        const total = task.totalNum || 0
        const processed = (task.successNum || 0) + (task.failNum || 0)
        const percentage = total > 0 ? Math.round((processed / total) * 100) : 0
        taskProgress.value = {
          processed,
          total,
          percentage,
          success: task.successNum || 0,
          fail: task.failNum || 0,
          status: percentage === 100 ? 'success' : 'primary'
        }
        if (task.status === 2 || task.status === 3) {
          clearInterval(pollingTimer.value)
          pollingTimer.value = null
        }
      }
    } catch (e) {
      console.error('轮询任务详情失败', e)
    }
  }, 2000)
}

// 创建批量任务
const handleCreateTask = async () => {
  if (fileList.value.length === 0) {
    ElMessage.warning('请至少选择一个图片文件')
    return
  }

  // 检查所有文件大小
  for (const file of fileList.value) {
    if (file.size > MAX_FILE_SIZE) {
      ElMessage.error(`文件 ${file.name} 大小超过 200MB 限制，请移除后重试`)
      return
    }
  }

  const formData = new FormData()
  if (taskName.value) formData.append('taskName', taskName.value)
  if (taskDesc.value) formData.append('taskDesc', taskDesc.value)
  formData.append('mode', mode.value)
  fileList.value.forEach(file => {
    formData.append('files', file.raw)
  })

  creating.value = true
  errorMsg.value = ''
  createdTask.value = null

  try {
    const resp = await uploadBatchTask(formData)
    if (resp.data.code === 200) {
      createdTask.value = resp.data.data
      taskProgress.value = {
        processed: 0,
        total: createdTask.value.totalNum || 0,
        percentage: 0,
        success: 0,
        fail: 0,
        status: 'primary'
      }
      ElMessage.success('任务创建成功，正在后台处理')
      // 清空已选文件列表
      fileList.value = []
      uploadRef.value?.clearFiles()
      // 开始轮询进度
      startPolling(createdTask.value.batchId)
    } else {
      errorMsg.value = resp.data.msg || '创建失败'
    }
  } catch (e) {
    console.error('上传失败', e)
    // 处理后端返回的错误信息
    if (e.response && e.response.data) {
      const errorData = e.response.data
      if (typeof errorData === 'object' && errorData.msg) {
        errorMsg.value = errorData.msg
      } else if (typeof errorData === 'string') {
        errorMsg.value = errorData
      } else {
        errorMsg.value = '请求失败，请检查后端服务'
      }
    } else {
      errorMsg.value = '网络错误，请稍后重试'
    }
  } finally {
    creating.value = false
  }
}

// 重置表单
const reset = () => {
  taskName.value = ''
  taskDesc.value = ''
  mode.value = 'filename-compare' // 重置时也设置为文件名比对模式
  fileList.value = []
  uploadRef.value?.clearFiles()
  createdTask.value = null
  errorMsg.value = ''
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
    pollingTimer.value = null
  }
}

// 查看批次日志
const viewBatchLogs = (batchId) => {
  router.push({path: '/history', query: {batchId}})
}

// 任务状态标签
const taskStatusTag = (status) => {
  const map = ['info', 'primary', 'success', 'danger']
  return map[status] || 'info'
}

const taskStatusText = (status) => {
  const map = ['待执行', '执行中', '已完成', '已终止']
  return map[status] || '未知'
}

onUnmounted(() => {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value)
  }
})
</script>

<style scoped>
/* 样式保持不变 */
.online-test {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.panel {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.left-form {
  padding-right: 10px;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.right-content {
  padding-left: 10px;
}

.mode-row {
  margin-bottom: 20px;
}

.mode-row label {
  display: block;
  margin-bottom: 8px;
}

.upload-area {
  margin-bottom: 16px;
}

.upload-hint {
  margin-bottom: 8px;
  text-align: right;
  font-size: 13px;
}

.upload-area :deep(.el-upload-list--picture-card) {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 4px;
}

.upload-area :deep(.el-upload-list--picture-card::-webkit-scrollbar) {
  width: 6px;
}

.upload-area :deep(.el-upload-list--picture-card::-webkit-scrollbar-thumb) {
  background: #ccc;
  border-radius: 3px;
}

.actions {
  display: flex;
  gap: 12px;
  margin-top: 10px;
  justify-content: flex-end;
}

.task-progress {
  margin-top: 20px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.task-name {
  font-weight: 500;
}

.progress-stats {
  margin-bottom: 8px;
}

.progress-detail {
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #606266;
  margin-top: 8px;
}

.progress-footer {
  margin-top: 16px;
  text-align: right;
}

@media (max-width: 768px) {
  .left-form,
  .right-content {
    padding-left: 0;
    padding-right: 0;
  }

  .right-content {
    margin-top: 20px;
  }

  .actions {
    justify-content: flex-start;
  }
}
</style>